'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HubSpotFormModal } from '@/components/hubspot/HubSpotFormModal';

type NavigationItem = {
  name: string;
  href: string;
  badge?: string;
  children?: { name: string; href: string }[];
};

const navigation: NavigationItem[] = [
  {
    name: 'Dental',
    href: '/dental',
    children: [
      { name: 'Splints', href: '/dental/splints' },
      { name: 'Trays', href: '/dental/trays' },
      { name: 'Models', href: '/dental/models' },
    ],
  },
  { name: 'Audio', href: '/audio' },
  { name: 'Smartphone scanning', href: '/smartphone-scanning' },
  {
    name: 'About',
    href: '/about',
    children: [
      { name: 'News', href: '/news' },
      { name: 'Careers', href: '/careers' },
    ],
  },
];

const bookDemoFormId = process.env.NEXT_PUBLIC_HUBSPOT_BOOK_DEMO_FORM_ID || '';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookDemoOpen, setBookDemoOpen] = useState(false);
  return (
    <header className="fixed top-0 w-full bg-white/40 backdrop-blur z-50">
      <nav className="mx-auto max-w-[90rem] px-6 lg:px-8" aria-label="Top">
        <div className="flex items-center justify-between py-4">
          {/* Left: mobile menu button + logo + desktop nav */}
          <div className="flex items-center gap-x-4 lg:gap-x-8">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-m-2.5 text-gray-700 lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close main menu' : 'Open main menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="h-6 w-6" aria-hidden="true" />
              )}
            </Button>
            <Link href="/" className="-m-1.5 p-1.5 flex items-center">
              <Image
                src="/logo-black.svg"
                alt="H3D Logo"
                className="h-8 w-auto"
                width={32}
                height={32}
              />
            </Link>
            <div className="hidden lg:flex lg:gap-x-8 lg:items-center">
              {navigation.map((item) => (
                <div key={item.name} className="relative group">
                  <Link
                    href={item.href}
                    className="text-sm font-semibold leading-6 text-gray-900 hover:text-blue-600 transition-colors flex items-center gap-1"
                  >
                    {item.name}
                    {item.badge && <Badge className="ml-1">{item.badge}</Badge>}
                    {item.children && (
                      <ChevronDown className="h-4 w-4 transition-transform group-hover:rotate-180" />
                    )}
                  </Link>
                  {/* Dropdown menu */}
                  {item.children && (
                    <div className="absolute left-0 mt-2 w-48 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 bg-white rounded-lg shadow-lg border">
                      <div className="py-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.name}
                            href={child.href}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600"
                          >
                            {child.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right: Contact us button (always visible) */}
          <Button
            variant="outline"
            className="rounded-full px-6 lg:px-8"
            onClick={() => setBookDemoOpen(true)}
          >
            Contact us
          </Button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t">
            <div className="space-y-2">
              {navigation.map((item) => (
                <div key={item.name}>
                  <Link
                    href={item.href}
                    className="block py-2 text-base font-semibold text-gray-900 hover:text-blue-600"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.name}
                    {item.badge && <Badge className="ml-2">{item.badge}</Badge>}
                  </Link>
                  {item.children && (
                    <div className="ml-4 space-y-2">
                      {item.children.map((child) => (
                        <Link
                          key={child.name}
                          href={child.href}
                          className="block py-1 text-sm text-gray-600 hover:text-blue-600"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          {child.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </nav>

      <HubSpotFormModal
        formId={bookDemoFormId}
        open={bookDemoOpen}
        onOpenChange={setBookDemoOpen}
        title="Contact us"
        description="Fill out the form below and our team will contact you as soon as we can."
      />
    </header>
  );
}
